import { findSuitableProduct } from './billing-utils';
import { findSuitablePrices } from '../utils/member-limits';
import { logError, setContext } from '../utils/sentry-logger';

/**
 * @typedef {import('../types').SiteLimits} SiteLimits
 * @typedef {import('../types').ProductWithPricesAndFeatures} ProductWithPricesAndFeatures
 * @typedef {import('../types').PriceWithFeatures} PriceWithFeatures
 * @typedef {import('../types').User} User
 * @typedef {import('../types').Subscription} Subscription
 * @typedef {import('../types').PendingSubscription} PendingSubscription
 */

/**
 * @typedef {object} PendingSubscriptionProps
 * @property {Subscription} subscription
 * @property {boolean} isAutoBillingGrace
 * @property {boolean} isIncomplete
 * @property {SiteLimits} currentSiteLimits
 * @property {ProductWithPricesAndFeatures[]} availableProducts
 * @property {User} user
 */

/**
 * @description Removes the pending subscription information from the local storage
 * @param {string} customerId
 * @return {void}
 * @private
 */
const _removeFromLocalStorage = (customerId) => {
    try {
        if (typeof window?.localStorage !== 'undefined') {
            window.localStorage.removeItem(`gh_checkout_${customerId}`);
        }
    } catch (error) {
        logError(error, {
            level: 'warning',
            contexts: {
                customerId,
            },
        });
    }
};

/**
 * @description Writes the pending subscription information to the local storage
 * @param {string} customerId
 * @param {PendingSubscription} pendingSubscription
 * @return {void}
 * @private
 */
const _writeToLocalStorage = (customerId, pendingSubscription) => {
    try {
        if (typeof window?.localStorage !== 'undefined') {
            window.localStorage.setItem(
                `gh_checkout_${customerId}`,
                JSON.stringify(pendingSubscription),
            );
        }
    } catch (error) {
        logError(error, {
            level: 'warning',
            contexts: {
                customerId,
                pendingSubscription,
            },
        });
    }
};

/**
 * @description Reads the pending subscription information from the local storage
 * @param {string} customerId
 * @returns {string|null} stringified pending subscription or null
 * @private
 */
const _readFromLocalStorage = (customerId) => {
    try {
        if (typeof window?.localStorage !== 'undefined') {
            return window.localStorage.getItem(`gh_checkout_${customerId}`);
        }
        return null;
    } catch (error) {
        logError(error, {
            level: 'warning',
            contexts: {
                customerId,
            },
        });

        return null;
    }
};

/**
 * @description Find a product by price id
 * @param {ProductWithPricesAndFeatures[]} products
 * @param {string} priceId
 * @returns {ProductWithPricesAndFeatures|undefined}
 * @private
 */
const _findProductByPriceId = (products, priceId) =>
    products.find((product) => product.prices.some((price) => price.stripe_price_id === priceId));

/**
 * @description Get the product ID from the price ID
 * @param {ProductWithPricesAndFeatures[]} availableProducts
 * @param {string} priceId
 * @returns {string|null}
 * @private
 */
const _getProductIDFromPriceId = (availableProducts, priceId) =>
    _findProductByPriceId(availableProducts, priceId)?.stripeId ?? null;

/**
 * @description Build a pending subscription object from component values
 * @param {object} params
 * @param {string} params.product - Product name (already lowercased)
 * @param {string} params.productId - Stripe product ID
 * @param {string} params.period - Billing period
 * @param {string} params.priceId - Stripe price ID
 * @param {string} params.group - Product group
 * @returns {PendingSubscription}
 * @private
 */
const _buildPendingSubscription = ({ product, productId, period, priceId, group }) => ({
    product,
    productId,
    period,
    priceId,
    group,
});

/**
 * @description Find suitable product and price based on pending subscription data and current limits
 * @param {object} params
 * @param {ProductWithPricesAndFeatures[]} params.availableProducts
 * @param {PendingSubscription} params.storedPendingSubscription
 * @param {string} params.showProductGroup
 * @param {SiteLimits} params.currentSiteLimits
 * @returns {{suitableProduct: ProductWithPricesAndFeatures|null, suitablePrice: PriceWithFeatures|null}}
 * @private
 */
const _findSuitableProductAndPrice = ({
    availableProducts,
    storedPendingSubscription,
    showProductGroup,
    currentSiteLimits,
}) => {
    if (
        !storedPendingSubscription ||
        !currentSiteLimits ||
        !availableProducts ||
        availableProducts.length === 0
    ) {
        return { suitableProduct: null, suitablePrice: null };
    }

    const { group, period, priceId, product: productName, productId } = storedPendingSubscription;
    const product = productName?.toLowerCase();

    if (!product || !group || !period) {
        return { suitableProduct: null, suitablePrice: null };
    }

    /** @type {ProductWithPricesAndFeatures|null} */
    let suitableProduct = null;
    /** @type {PriceWithFeatures|null} */
    let suitablePrice = null;

    if (productId) {
        const pendingBaseProduct = availableProducts.find(
            (availableProduct) => availableProduct.stripeId === productId,
        );

        if (!pendingBaseProduct) {
            return { suitableProduct: null, suitablePrice: null };
        }

        // get the suitable price for the current product and member usage
        /** @type {PriceWithFeatures[]} */
        const suitablePrices = findSuitablePrices(
            pendingBaseProduct?.prices,
            currentSiteLimits?.members?.total,
            period,
        );
        suitablePrice = suitablePrices?.[0];

        if (!suitablePrice) {
            return { suitableProduct: null, suitablePrice: null };
        }

        suitableProduct = availableProducts.find(
            (availableProduct) => availableProduct?.stripeId === productId,
        );
    } else if (showProductGroup && showProductGroup === group) {
        // TODO: [GRACE PERIOD] - remove this branch after a few days of grace period when this code has been deployed to production
        const pendingBaseProducts = availableProducts.filter((availableProduct) => {
            return (
                availableProduct.name.toLowerCase() === product && availableProduct.group === group
            );
        });

        if (pendingBaseProducts.length === 0) {
            return { suitableProduct: null, suitablePrice: null };
        }

        // When not identified by a product id and the customer is traversing through a legacy product, we need to find
        // the correct product by price id that matches the pending subscription
        const pendingBaseProduct = _findProductByPriceId(pendingBaseProducts, priceId);

        if (!pendingBaseProduct) {
            return { suitableProduct: null, suitablePrice: null };
        }

        // get the suitable price for the current product and member usage
        /** @type {PriceWithFeatures[]} */
        const suitablePrices = findSuitablePrices(
            pendingBaseProduct?.prices,
            currentSiteLimits?.members?.total,
            period,
        );
        suitablePrice = suitablePrices?.[0];

        if (!suitablePrice) {
            return { suitableProduct: null, suitablePrice: null };
        }

        suitableProduct = pendingBaseProduct;
    } else {
        // get the suitable product for the current member usage if we don't have enough information to identify it directly
        const suitableProductAndPrice = findSuitableProduct(
            availableProducts,
            currentSiteLimits,
            period,
            showProductGroup,
        );
        suitableProduct = suitableProductAndPrice?.product;
        suitablePrice = suitableProductAndPrice?.price;
    }

    return { suitableProduct, suitablePrice };
};

/**
 * @description Get the pending subscription information from the local storage
 * and validate it against the current site limits and assigned product group.
 * If the pending subscription is not valid any more, it will be removed from the local storage.
 *
 * There are different cases of when we require and deal with a pending subscription, which is stores
 * locally in the browser's local storage:
 *
 * 1. Customer is in an incomplete state, which happens only for trial users trying to subscribe
 *    to a paid plan, but the SCA confirmation failed or wasn't completed. The other case is when
 *    a offline payment fails due to SCA confirmation, which can happen for automatic billing upgrades
 *    for our customers who use the autobilling mode. In both cases, the incomplete subscription is
 *    already stored on the user's subscription object (as the base product), which makes it easier
 *    to restore a pending subscription from that data. The incomplete subscription expires automatically
 *    after 23 hours in Stripe and the subscription status changes to `incomplete_expired`, which we handle
 *    the same as a cancellation.
 *
 * 2. A paying customer failed the SCA payment during the checkout process, but is already on a paid plan
 *    and it wasn't an autobilling upgrade. In this case, the pending subscription is only stored in the
 *    local storage, and we have to validate that the pending subscription is still valid based on the
 *    current site limits and assigned product group. If it's not valid any more, we remove it from the local storage.
 *    Invoices for a pending subscriptions are set to void after 23 hours automatically by Stripe. After that the
 *    customer is in a state as if nothing happened.
 *
 * The pending subscription object is also passed to the `get-checkout-route` function, which uses this information
 * to build the correct checkout route for the customer to retry the payment. The checkout route is also communicated
 * to Ghost Admin and used in notifications/modals to link to the correct product in the checkout directly.
 *
 * @param {PendingSubscriptionProps} props
 * @returns {PendingSubscription|null}
 */
export default function getPendingSubscription(props) {
    const {
        subscription,
        isAutoBillingGrace,
        isIncomplete,
        currentSiteLimits,
        availableProducts,
        user,
    } = props;
    const customerId = subscription?.customer_id;

    setContext('getPendingSubscription', {
        subscriptionId: subscription?.id,
        customerId,
        isAutoBillingGrace,
        isIncomplete,
    });

    if (!customerId) {
        return null;
    }

    /** @type {PendingSubscription} */
    let pendingSubscription = null;
    /** @type {string|null} */
    const storedPendingSubscriptionString = _readFromLocalStorage(customerId);
    const pendingSubscriptionIsRequired = isAutoBillingGrace || isIncomplete;
    const baseProduct = subscription?.meta?.baseProduct;

    if (!storedPendingSubscriptionString && !pendingSubscriptionIsRequired) {
        return null;
    }

    try {
        /** @type {PendingSubscription} */
        const storedPendingSubscription = storedPendingSubscriptionString
            ? JSON.parse(storedPendingSubscriptionString)
            : null;

        setContext('getPendingSubscription', {
            pendingSubscription: storedPendingSubscription,
        });

        // Try to build the pending subscription from the subscription's base product for incomplete/autobilling grace states
        if (pendingSubscriptionIsRequired && !storedPendingSubscription) {
            if (
                !baseProduct ||
                (typeof baseProduct === 'object' && Object.keys(baseProduct).length === 0)
            ) {
                logError(
                    new Error(
                        'Cannot build pending subscription for incomplete/autobilling grace subscription without base product',
                    ),
                    {
                        level: 'warning',
                        contexts: {
                            subscriptionId: subscription?.id,
                            customerId,
                        },
                    },
                );

                return null;
            }

            pendingSubscription = _buildPendingSubscription({
                product: baseProduct?.name?.toLowerCase(),
                productId: baseProduct?.stripe_product_id,
                period: baseProduct?.billing_period,
                priceId: baseProduct?.stripe_price_id,
                group: baseProduct?.group,
            });

            _writeToLocalStorage(subscription?.customer_id, pendingSubscription);

            return pendingSubscription;
        }

        if (!storedPendingSubscription) {
            return null;
        }

        if (pendingSubscriptionIsRequired) {
            // Backfill productId from priceId if it's missing (legacy data)
            const resolvedProductId =
                storedPendingSubscription?.productId ??
                _getProductIDFromPriceId(availableProducts, storedPendingSubscription.priceId);

            return _buildPendingSubscription({
                product: storedPendingSubscription?.product,
                productId: resolvedProductId,
                period: storedPendingSubscription?.period,
                priceId: storedPendingSubscription?.priceId,
                group: storedPendingSubscription?.group,
            });
        }

        // verify the pending subscription still matches with what the user can have
        /** @type {string|undefined} */
        const showProductGroup = user?.show_product_group;

        const { suitableProduct, suitablePrice } = _findSuitableProductAndPrice({
            availableProducts,
            storedPendingSubscription,
            showProductGroup,
            currentSiteLimits,
        });

        const {
            group,
            period,
            priceId,
            product: productName,
            productId,
        } = storedPendingSubscription;
        const product = productName?.toLowerCase();

        if (suitableProduct && suitablePrice) {
            // Check if stored subscription still matches suitable product/price
            const identifyByProductId = productId !== undefined && productId !== null;
            const isProductMismatch = identifyByProductId
                ? suitableProduct.stripeId !== productId
                : suitableProduct.name.toLowerCase() !== product;
            const isPeriodMismatch = suitablePrice.billing_period !== period;
            const isPriceMismatch = suitablePrice.stripe_price_id !== priceId;
            const isGroupMismatch = suitableProduct.group !== group;

            if (isProductMismatch || isPeriodMismatch || isPriceMismatch || isGroupMismatch) {
                // Case: The pending subscription is not valid any more, remove it from local storage
                _removeFromLocalStorage(subscription?.customer_id);

                return null;
            }

            // Case: stored pending subscription is still valid
            pendingSubscription = _buildPendingSubscription({
                product,
                productId: productId ?? suitableProduct?.stripeId,
                period,
                priceId,
                group,
            });
        }
    } catch (error) {
        logError(error, {
            level: 'error',
            contexts: {
                subscriptionId: subscription?.id,
                customerId,
            },
        });
    }

    return pendingSubscription;
}
